/**
 * @fileoverview Zod schema for Sales Pipeline's "New Opportunity" (Create Prospect) form.
 * @description "Creating a prospect creates its tenant record in Prospect status" — captures the
 * full field set the design bundle's 5-step form specifies (Basic Info, Address, Organisation,
 * Primary Contact, Sales Info), matching the structure the now-legacy NewOpportunityForm documented
 * before the Tenant-model rewrite. Basic Info requires name/bankType/headOffice/regNumber and
 * leaves website optional; Address requires every field (address, state, city, district and a
 * 6-digit pincode); Organisation requires branches and the Core Banking Solution, leaving
 * employees/existingMis optional. Primary Contact has no office phone: the contact's own mobile is
 * the one number captured. Bank Tier is never submitted from this form: it's always derived
 * from branches and shown read-only. Bank type, state, core banking solution and lead source are
 * catalogue ids from the backend's prospect metadata (GET /tenants/prospect-metadata), and the
 * lead rating is the backend's HOT/WARM/COLD, so the values map 1:1 onto Create Prospect's body. Expected Deal Size stays excluded — Sales Information's own
 * fields are lead source/rating/sales-responsibility/notes, not deal sizing. Every issue message is
 * a translation key (per this repo's i18n rule for validators), translated only at the display call
 * site in CreateProspectForm.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { z } from 'zod'

import { emailField, mobileNumberField } from '@/shared/lib/validation/contact-fields.schema'

export const createProspectFormSchema = z.object({
  name: z.string().trim().min(1, 'sales.pipeline.create.errors.nameRequired'),
  /** A bank type id from the prospect metadata. */
  bankTypeId: z.string().trim().min(1, 'sales.pipeline.create.errors.bankTypeRequired'),
  headOffice: z.string().trim().min(1, 'sales.pipeline.create.errors.headOfficeRequired'),
  regNumber: z.string().trim().min(1, 'sales.pipeline.create.errors.regNumberRequired'),
  website: z.string().trim().optional(),
  /** A state or union territory id from the prospect metadata. */
  stateId: z.string().trim().min(1, 'sales.pipeline.create.errors.stateRequired'),
  city: z.string().trim().min(1, 'sales.pipeline.create.errors.cityRequired'),
  district: z.string().trim().min(1, 'sales.pipeline.create.errors.districtRequired'),
  pincode: z
    .string()
    .trim()
    .min(1, 'sales.pipeline.create.errors.pincodeRequired')
    .regex(/^[1-9]\d{5}$/, 'sales.pipeline.create.errors.pincodeInvalid'),
  address: z.string().trim().min(1, 'sales.pipeline.create.errors.addressRequired'),
  branches: z.number().int().min(1, 'sales.pipeline.create.errors.branchesRequired'),
  employees: z.number().int().min(0).optional(),
  /** A core banking solution id from the prospect metadata. */
  coreBankingSolutionId: z
    .string()
    .trim()
    .min(1, 'sales.pipeline.create.errors.coreBankingRequired'),
  existingMis: z.string().trim().optional(),
  contactName: z.string().trim().min(1, 'sales.pipeline.create.errors.contactNameRequired'),
  contactDesignation: z.string().trim().optional(),
  contactEmail: emailField({
    required: 'sales.pipeline.create.errors.contactEmailRequired',
    invalid: 'sales.pipeline.create.errors.contactEmailInvalid',
  }),
  contactPhone: mobileNumberField(),
  /** A lead source id from the prospect metadata. */
  leadSourceId: z.string().trim().min(1, 'sales.pipeline.create.errors.leadSourceRequired'),
  leadRating: z.enum(['HOT', 'WARM', 'COLD']),
  notes: z.string().trim().optional(),
  /** The user id of the Sales Executive to name as owner at creation, if any — optional, and only
   * offered to a caller who may assign one (never a Sales Executive, whose submission is reviewed
   * first). A prospect left unassigned can still be assigned later. */
  proposedOwnerUserId: z.string().trim().optional(),
})

export type CreateProspectFormValues = z.infer<typeof createProspectFormSchema>
